/**
 * ToolCallsBlock — Collapsible reasoning block for tool calls.
 *
 * Renders all tool-call parts from the current message as compact rows
 * inside a bordered, collapsible card. Replaces the per-tool ToolCard/ToolFallback
 * rendering with a unified "Used N tools" block.
 *
 * Design: docs/prd/progress-tracker/design.html → Reasoning block examples
 */

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  BrainIcon,
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
  LoaderIcon,
  LockIcon,
  XIcon,
} from 'lucide-react';
import { useAuiState, useThread, useThreadList } from '@assistant-ui/react';
import { useLangGraphInterruptState } from '@assistant-ui/react-langgraph';
import { cn } from '@/client/lib/utils';
import { useStreamingStatus } from '@/client/lib/streamingStatusContext';
import { useOptionalAuth } from '@/client/app/providers/auth-context';

// ---------------------------------------------------------------------------
// Friendly tool name map
// ---------------------------------------------------------------------------

/**
 * Friendly tool names — must match the `label` prop from each ToolCard in tool-uis.tsx.
 * When adding a new tool UI, add its display name here too.
 */
const TOOL_DISPLAY_NAMES: Record<string, string> = {
  // Filesystem
  list_directory: 'Browse Files',
  read_file: 'Read File',
  write_file: 'Write File',
  edit_file: 'Edit File',
  delete_file: 'Delete File',
  // Knowledge & context
  load_skill: 'Load Knowledge',
  get_context_source: 'Get Context',
  // Data & query
  query: 'Analyze Data',
  execute_query: 'Analyze Data',
  export_data: 'Export Data',
  get_table_metadata: 'Look Up Data',
  // Reports
  create_report: 'Create Report',
  // Guidance
  list_guidances: 'List Guidances',
  get_guidance: 'Read Guidance',
  update_draft_guidance: 'Update Guidance',
  create_draft_guidance: 'Create Draft Guidance',
  publish_guidance: 'Publish Guidance',
  list_guidance_templates: 'Guidance Templates',
  // Skills
  list_skills: 'List Skills',
  get_skill: 'Read Skill',
  create_draft_skill: 'Create Draft Skill',
  update_draft_skill: 'Update Skill',
  publish_skill: 'Publish Skill',
  list_intents: 'List Intents',
  // AI Agent settings
  list_agent_configurations: 'List Stores',
  get_agent_configuration: 'Agent Configuration',
  update_tone_of_voice: 'Update Tone of Voice',
  update_excluded_handover_topics: 'Update Handover Topics',
  // Merchant website
  get_merchant_sitemap: 'Merchant Sitemap',
  read_merchant_website: 'Read Website',
  get_product_catalog: 'Product Catalog',
  // Opportunities
  list_opportunities: 'List Opportunities',
  get_opportunity: 'Get Opportunity',
  process_opportunity: 'Process Opportunity',
  // Support actions
  list_support_actions: 'List Actions',
  get_support_action: 'Get Action',
  create_support_action: 'Create Action',
  update_support_action: 'Update Action',
  enable_support_action: 'Enable Action',
  disable_support_action: 'Disable Action',
  convert_to_advanced_view: 'Advanced View',
  list_action_templates: 'List Templates',
  get_action_template: 'Get Template',
  create_action_from_template: 'Deploy Template',
  // Navigation
  navigate_user: 'Navigate',
  // Integrations
  initiate_integration: 'Connect Integration',
  // Onboarding
  trigger_celebration: 'Celebration',
  // Export
  export_file: 'Export File',
  // Preview
  preview_ai_agent: 'Preview AI Agent',
  preview_tov: 'Preview Tone of Voice',
  // Slack
  send_slack_message: 'Send Slack Message',
  read_current_thread: 'Read Thread',
  read_slack_channel: 'Read Channel',
  search_slack_user: 'Search Slack User',
  search_slack_channel: 'Search Slack Channel',
  // Cortex misc
  generate_final_answer: 'Final Answer',
  ask_clarifying_questions: 'Clarifying Questions',
  web_search: 'Web Search',
  web_fetch: 'Fetch URL',
  generate_signed_gcs_link: 'Generate Link',
  provide_sql: 'Provide SQL',
  provide_feedback: 'Provide Feedback',
  set_thinking_mode: 'Thinking Mode',
  // Subagent
  research_subagent: 'Research (delegated)',
  // Tickets
  create_gorgias_ticket: 'Create Ticket',
  // Plan
  write_plan: 'Update Plan',
  // Tasks
  upsert_task: 'Propose Change',
  submit_tasks: 'Submit for Review',
  execute_task: 'Execute Task',
};

function getToolDisplayName(toolName: string): string {
  return TOOL_DISPLAY_NAMES[toolName] || toolName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Tool detail text — mirrors the `detail` prop computed by each ToolCard
// ---------------------------------------------------------------------------

/** Running-state detail text per tool (what the tool shows while executing) */
const TOOL_RUNNING_DETAIL: Record<string, string | ((args: Record<string, unknown>) => string)> = {
  list_directory: 'Browsing files...',
  read_file: 'Reading file...',
  write_file: 'Writing file...',
  edit_file: 'Editing file...',
  delete_file: 'Deleting...',
  load_skill: (args) => args.name ? `Loading "${args.name}"...` : 'Loading...',
  get_context_source: 'Loading context...',
  query: 'Analyzing data...',
  execute_query: 'Analyzing data...',
  export_data: 'Exporting data...',
  get_table_metadata: 'Loading schema...',
  create_report: 'Creating report...',
  list_guidances: 'Loading guidances...',
  get_guidance: 'Loading guidance...',
  update_draft_guidance: 'Updating guidance...',
  create_draft_guidance: 'Creating guidance...',
  publish_guidance: 'Publishing...',
  list_skills: 'Loading skills...',
  get_skill: 'Loading skill...',
  create_draft_skill: 'Creating skill...',
  update_draft_skill: 'Updating skill...',
  publish_skill: 'Publishing skill...',
  list_intents: 'Loading intents...',
  list_agent_configurations: 'Loading stores...',
  get_agent_configuration: 'Loading config...',
  list_support_actions: 'Loading actions...',
  get_support_action: 'Loading action...',
  create_support_action: 'Creating action...',
  update_support_action: 'Updating action...',
  enable_support_action: 'Enabling action...',
  disable_support_action: 'Disabling action...',
  navigate_user: 'Navigating...',
  initiate_integration: (args) => {
    const t = typeof args.integration_type === 'string' ? args.integration_type : '';
    return t ? `Setting up ${t}...` : 'Setting up integration...';
  },
  trigger_celebration: 'Preparing celebration...',
  send_slack_message: 'Sending message...',
  export_file: 'Exporting file...',
  research_subagent: 'Investigating...',
  write_plan: 'Updating plan...',
  upsert_task: 'Proposing change...',
  submit_tasks: 'Submitting for review...',
  execute_task: 'Executing task...',
};

function parseResult(result: unknown): Record<string, unknown> | undefined {
  if (result == null) return undefined;
  if (typeof result === 'string') {
    try { return JSON.parse(result); } catch { return undefined; }
  }
  if (typeof result === 'object' && !Array.isArray(result)) return result as Record<string, unknown>;
  return undefined;
}

/**
 * Completed-state detail text per tool — mirrors the `detail` prop from each
 * ToolCard render function in tool-uis.tsx. The function variants receive
 * parsed args and raw result from the tool-call part.
 */
const TOOL_COMPLETE_DETAIL: Record<string, string | ((args: Record<string, unknown>, result: unknown) => string)> = {
  // Filesystem
  list_directory: (_args, result) => {
    const parsed = parseResult(result);
    const count = Array.isArray(parsed) ? parsed.length : 0;
    return count > 0 ? `${count} item${count !== 1 ? 's' : ''} found` : 'No items found';
  },
  read_file: 'File loaded',
  write_file: 'File written',
  edit_file: 'File updated',
  delete_file: 'File deleted',
  // Knowledge
  load_skill: (args) => args.name ? `"${args.name}" knowledge loaded` : 'Knowledge loaded',
  get_context_source: 'Context loaded',
  // Data — parse result.count for row count
  query: (_args, result) => {
    const parsed = parseResult(result);
    const count = parsed?.count ?? 0;
    return `Found ${count} result${count !== 1 ? 's' : ''}`;
  },
  execute_query: (_args, result) => {
    const parsed = parseResult(result);
    const count = parsed?.count ?? 0;
    return `Found ${count} result${count !== 1 ? 's' : ''}`;
  },
  export_data: 'Export ready',
  get_table_metadata: 'Data loaded',
  // Reports
  create_report: (_args, result) => {
    const parsed = parseResult(result);
    return parsed?.title ? String(parsed.title) : 'Report created';
  },
  // Guidance
  list_guidances: (_args, result) => {
    const parsed = parseResult(result);
    const count = Array.isArray(parsed?.items) ? parsed.items.length : parsed?.count;
    return count != null ? `${count} guidances` : 'Guidances loaded';
  },
  get_guidance: (_args, result) => {
    const parsed = parseResult(result);
    return parsed?.title ? String(parsed.title) : 'Guidance loaded';
  },
  update_draft_guidance: 'Guidance updated',
  create_draft_guidance: (args) => args.title ? `"${args.title}" created` : 'Draft created',
  publish_guidance: 'Guidance published',
  list_guidance_templates: 'Templates loaded',
  // Skills
  list_skills: (_args, result) => {
    const parsed = parseResult(result);
    const count = Array.isArray(parsed?.items) ? parsed.items.length : parsed?.count;
    return count != null ? `${count} skills` : 'Skills loaded';
  },
  get_skill: (_args, result) => {
    const parsed = parseResult(result);
    return parsed?.title ? String(parsed.title) : 'Skill loaded';
  },
  create_draft_skill: (args) => args.title ? `"${args.title}" created` : 'Draft created',
  update_draft_skill: 'Skill updated',
  publish_skill: 'Skill published',
  list_intents: (_args, result) => {
    const parsed = parseResult(result);
    const count = Array.isArray(parsed?.items) ? parsed.items.length : parsed?.count;
    return count != null ? `${count} intents` : 'Intents loaded';
  },
  // AI Agent
  list_agent_configurations: (_args, result) => {
    const parsed = parseResult(result);
    const count = parsed?.count;
    return count != null ? `${count} store${count !== 1 ? 's' : ''}` : 'Stores loaded';
  },
  get_agent_configuration: 'Configuration loaded',
  update_tone_of_voice: 'Tone updated',
  update_excluded_handover_topics: 'Topics updated',
  // Merchant
  get_merchant_sitemap: 'Sitemap loaded',
  read_merchant_website: (args) => {
    const page = args.url ? String(args.url).replace(/^https?:\/\//, '').slice(0, 40) : '';
    return page ? `Read ${page}` : 'Page loaded';
  },
  get_product_catalog: (_args, result) => {
    const parsed = parseResult(result);
    const count = parsed?.total_products;
    return count != null ? `${count} products loaded` : 'Catalog loaded';
  },
  // Opportunities
  list_opportunities: (_args, result) => {
    const parsed = parseResult(result);
    const count = parsed?.count;
    return count != null ? `${count} opportunities` : 'Opportunities loaded';
  },
  get_opportunity: 'Opportunity loaded',
  process_opportunity: 'Opportunity processed',
  // Actions
  list_support_actions: (_args, result) => {
    const parsed = parseResult(result);
    const count = Array.isArray(parsed?.actions) ? parsed.actions.length : parsed?.count;
    return count != null ? `${count} actions` : 'Actions loaded';
  },
  get_support_action: (_args, result) => {
    const parsed = parseResult(result);
    return parsed?.name ? String(parsed.name) : 'Action loaded';
  },
  create_support_action: 'Action created',
  update_support_action: 'Action updated',
  enable_support_action: 'Action enabled',
  disable_support_action: 'Action disabled',
  convert_to_advanced_view: 'Converted to Advanced View',
  list_action_templates: 'Templates loaded',
  get_action_template: 'Template loaded',
  create_action_from_template: 'Template deployed',
  // Navigation
  navigate_user: 'Page opened',
  // Integrations
  initiate_integration: (args) => {
    const t = typeof args.integration_type === 'string' ? args.integration_type : '';
    return t ? `${t.charAt(0).toUpperCase()}${t.slice(1)} connected` : 'Integration connected';
  },
  // Onboarding
  trigger_celebration: 'Onboarding complete',
  // Slack
  send_slack_message: 'Message sent',
  read_current_thread: 'Thread loaded',
  read_slack_channel: 'Channel loaded',
  search_slack_user: 'User found',
  search_slack_channel: 'Channel found',
  // Misc
  generate_final_answer: 'Answer ready',
  ask_clarifying_questions: 'Questions sent',
  web_search: 'Search complete',
  web_fetch: 'Content loaded',
  generate_signed_gcs_link: 'Link ready',
  provide_sql: 'SQL ready',
  provide_feedback: 'Feedback recorded',
  set_thinking_mode: 'Mode updated',
  research_subagent: 'Investigation complete',
  export_file: 'File exported',
  create_gorgias_ticket: 'Ticket created',
  write_plan: (args) => args.title ? String(args.title) : 'Plan updated',
  upsert_task: (args) => args.title ? String(args.title) : 'Change proposed',
  submit_tasks: 'Awaiting review',
  execute_task: (args) => args.task_id ? `Executed: ${args.task_id}` : 'Task executed',
};

function getToolDetail(part: ToolCallPart): string {
  const isRunning = part.status?.type === 'running';
  const isPending = part.status?.type === 'requires-action';
  const isError = part.status?.type === 'incomplete';

  if (isPending) return 'Awaiting approval';

  if (isError) {
    const err = part.status?.error;
    return typeof err === 'string' ? err.slice(0, 80) : 'Error';
  }

  if (isRunning) {
    const runningDetail = TOOL_RUNNING_DETAIL[part.toolName];
    if (typeof runningDetail === 'function') {
      const args = part.argsText ? (parseResult(part.argsText) ?? {}) : {};
      return runningDetail(args);
    }
    return runningDetail ?? '';
  }

  // Complete — use mapped description
  const args = part.argsText ? (parseResult(part.argsText) ?? {}) : {};
  const mapped = TOOL_COMPLETE_DETAIL[part.toolName];
  if (mapped) {
    if (typeof mapped === 'function') return mapped(args, part.result);
    return mapped;
  }

  // Fallback: use first line of raw result string
  if (typeof part.result === 'string' && part.result.trim()) {
    const firstLine = part.result.split('\n').find((l) => l.trim()) ?? '';
    if (firstLine.length <= 80) return firstLine;
    return firstLine.slice(0, 77) + '...';
  }

  return '';
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ToolCallPart {
  type: 'tool-call';
  toolName: string;
  argsText?: string;
  result?: unknown;
  status?: { type: string; error?: unknown; reason?: string };
}

interface TextPart {
  type: 'text';
  text: string;
}

/** A part that appears in the reasoning block — either a tool call or intermediate text. */
type ReasoningPart =
  | (ToolCallPart & { _kind: 'tool' })
  | (TextPart & { _kind: 'text' });

// ---------------------------------------------------------------------------
// Tool status icon
// ---------------------------------------------------------------------------

type ResolvedStatus = 'running' | 'complete' | 'cancelled' | 'error' | 'pending';

function resolveToolStatus(status?: { type: string; reason?: string; error?: unknown }, hasPendingInterrupt?: boolean): ResolvedStatus {
  const type = status?.type ?? 'complete';

  if (type === 'running') return 'running';

  if (type === 'requires-action') {
    // Real HITL interrupt → pending approval
    if (hasPendingInterrupt) return 'pending';
    // Spurious requires-action (stop/double-text, no result) → cancelled
    return 'cancelled';
  }

  if (type === 'incomplete') {
    // Cancelled by user or stream interruption
    if (status?.reason === 'cancelled') return 'cancelled';
    // Actual error
    if (status?.error) return 'error';
    // No result, no error — treat as cancelled
    return 'cancelled';
  }

  return 'complete';
}

function ToolStatusIcon({ resolvedStatus }: { resolvedStatus: ResolvedStatus }) {
  if (resolvedStatus === 'running') {
    return (
      <span className="flex size-3 shrink-0 items-center justify-center rounded-full border-[1.5px] border-primary bg-background">
        <LoaderIcon className="size-2 animate-spin text-primary" />
      </span>
    );
  }
  if (resolvedStatus === 'error') {
    return (
      <span className="flex size-3 shrink-0 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
        <XIcon className="size-2" strokeWidth={3} />
      </span>
    );
  }
  if (resolvedStatus === 'cancelled') {
    return (
      <span className="flex size-3 shrink-0 items-center justify-center rounded-full border-[1.5px] border-muted-foreground/40 bg-background">
        <XIcon className="size-2 text-muted-foreground" />
      </span>
    );
  }
  if (resolvedStatus === 'pending') {
    return (
      <span className="flex size-3 shrink-0 items-center justify-center rounded-full border-[1.5px] border-warning bg-background">
        <ClockIcon className="size-2 text-warning" />
      </span>
    );
  }
  // complete
  return (
    <span className="flex size-3 shrink-0 items-center justify-center rounded-full bg-success text-success-foreground">
      <CheckIcon className="size-2" strokeWidth={3} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Compact tool row with connector line + expandable details
// ---------------------------------------------------------------------------

function CompactToolRow({
  part,
  streamingStatus,
  hasPendingInterrupt,
}: {
  part: ToolCallPart;
  streamingStatus?: string | null;
  hasPendingInterrupt?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const displayName = getToolDisplayName(part.toolName);
  const resolved = resolveToolStatus(part.status, hasPendingInterrupt);
  const isRunning = resolved === 'running';
  const isError = resolved === 'error';
  const isCancelled = resolved === 'cancelled';
  const isPending = resolved === 'pending';

  // Inline description
  let inlineDetail: string;
  if (isRunning && streamingStatus) {
    inlineDetail = streamingStatus;
  } else if (isCancelled) {
    inlineDetail = 'Cancelled';
  } else {
    inlineDetail = getToolDetail(part);
  }

  // Expandable content: args + raw result
  const hasExpandable = !isRunning && (part.argsText || part.result != null);

  return (
    <div data-tool-row className="relative flex items-start gap-2.5 py-1.5">
      {/* Status icon */}
      <div className="relative z-10 mt-0.5">
        <ToolStatusIcon resolvedStatus={resolved} />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        {/* Name + inline detail row */}
        <button
          type="button"
          className={cn(
            'flex w-full items-center gap-1.5 text-left',
            hasExpandable && 'cursor-pointer',
          )}
          onClick={hasExpandable ? () => setExpanded((v) => !v) : undefined}
        >
          <span className="shrink-0 text-[12px] font-medium text-foreground">{displayName}</span>
          {inlineDetail && (
            <span
              className={cn(
                'truncate text-[11px]',
                isError ? 'text-destructive' : isCancelled ? 'text-muted-foreground line-through' : isPending ? 'text-warning' : 'text-muted-foreground',
              )}
            >
              — {inlineDetail}
            </span>
          )}
          {hasExpandable && (
            <ChevronDownIcon
              className={cn(
                'ml-auto size-3 shrink-0 text-muted-foreground opacity-40 transition-transform duration-150',
                expanded && 'rotate-180',
              )}
            />
          )}
        </button>

        {/* Expandable args + result */}
        {expanded && hasExpandable && (
          <div className="mt-1.5 space-y-1.5 rounded-md bg-muted/40 px-2.5 py-2 text-[11px]">
            {part.argsText && (
              <div>
                <div className="mb-0.5 font-medium text-muted-foreground">Args</div>
                <pre className="whitespace-pre-wrap break-all text-foreground/80">{part.argsText}</pre>
              </div>
            )}
            {part.result != null && (
              <div>
                <div className="mb-0.5 font-medium text-muted-foreground">Result</div>
                <pre className="whitespace-pre-wrap break-all text-foreground/80">
                  {typeof part.result === 'string' ? part.result : JSON.stringify(part.result, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tool calls body — renders rows with a single continuous connector line
// ---------------------------------------------------------------------------

function ToolCallsBody({
  reasoningParts,
  streamingStatus,
  hasPendingInterrupt,
}: {
  reasoningParts: ReasoningPart[];
  streamingStatus: string | null;
  hasPendingInterrupt: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);

  // Position the connector line from first tool icon center to last tool icon center
  useLayoutEffect(() => {
    const container = containerRef.current;
    const line = lineRef.current;
    if (!container || !line) return;

    const toolRows = container.querySelectorAll<HTMLElement>('[data-tool-row]');
    if (toolRows.length < 2) {
      line.style.display = 'none';
      return;
    }

    const first = toolRows[0];
    const last = toolRows[toolRows.length - 1];
    const containerRect = container.getBoundingClientRect();

    // Icon center is ~14px from top of each tool row (py-1.5=6 + mt-0.5=2 + half icon=6)
    const iconCenter = 14;
    const top = first.getBoundingClientRect().top - containerRect.top + iconCenter;
    const bottom = last.getBoundingClientRect().top - containerRect.top + iconCenter;

    line.style.display = '';
    line.style.top = `${top}px`;
    line.style.height = `${bottom - top}px`;
  });

  return (
    <div ref={containerRef} className="relative border-t px-3 pb-2 pt-1.5">
      {/* Single continuous connector line */}
      <div
        ref={lineRef}
        className="pointer-events-none absolute left-[17.5px] w-px bg-border"
        aria-hidden
      />
      {reasoningParts.map((part, i) => {
        if (part._kind === 'text') {
          return (
            <p key={i} className="py-1 pl-6 text-[11px] italic leading-snug text-muted-foreground">
              {part.text}
            </p>
          );
        }
        return (
          <CompactToolRow
            key={i}
            part={part}
            streamingStatus={part.status?.type === 'running' ? streamingStatus : null}
            hasPendingInterrupt={hasPendingInterrupt}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ToolCallsBlock
// ---------------------------------------------------------------------------

const EMPTY_PARTS: ReasoningPart[] = [];

/** "View tools" debug setting — visible only for Gorgias users, on by default for them. */
function useViewToolsDebug(): boolean {
  const auth = useOptionalAuth();
  const isGorgias = auth?.userEmail?.endsWith('@gorgias.com') ?? false;
  return useMemo(() => {
    if (!isGorgias) return false;
    try {
      const stored = localStorage.getItem('gaia-view-tools');
      return stored === null ? true : stored === 'true';
    } catch { return true; }
  }, [isGorgias]);
}

export function ToolCallsBlock() {
  const isRunning = useThread((s) => s.isRunning);
  const viewToolsDebug = useViewToolsDebug();
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const prevRef = useRef<ReasoningPart[]>(EMPTY_PARTS);

  // Detect real interrupts (task_review or legacy HITL) vs spurious requires-action on run end
  const interrupt = useLangGraphInterruptState();
  const interruptValue = interrupt?.value as Record<string, unknown> | undefined;
  const hasPendingInterrupt = !!(
    interruptValue?.type === 'task_review' ||
    interruptValue?.type === 'integration_setup' ||
    (interruptValue as { action_requests?: unknown[] } | undefined)?.action_requests?.length
  );

  // Read custom streaming status (from get_stream_writer)
  const { getStatus } = useStreamingStatus();
  const mainThreadId = useThreadList((s) => s.mainThreadId);
  const threadItems = useThreadList((s) => s.threadItems);
  const currentThread = threadItems[mainThreadId];
  const currentThreadId = currentThread?.externalId ?? currentThread?.remoteId;
  const streamingStatus = getStatus(currentThreadId);

  // Read tool-call parts AND intermediate text parts from the current message.
  // Text parts between tool calls are the agent's thinking/reasoning.
  // The last text part is the final answer — excluded (rendered by SmartText).
  const reasoningParts = useAuiState((s) => {
    try {
      const parts = s.message.parts;
      if (!parts) return prevRef.current;

      // Find last text index (final answer — excluded from reasoning block)
      let lastTextIdx = -1;
      for (let i = parts.length - 1; i >= 0; i--) {
        if (parts[i].type === 'text') { lastTextIdx = i; break; }
      }

      const hasToolCalls = parts.some((p) => p.type === 'tool-call');
      if (!hasToolCalls) return EMPTY_PARTS;

      // Collect tool calls + intermediate text (not the final answer)
      const collected: ReasoningPart[] = [];
      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        if (p.type === 'tool-call') {
          collected.push({ ...(p as ToolCallPart), _kind: 'tool' });
        } else if (p.type === 'text' && i !== lastTextIdx) {
          const text = (p as TextPart).text?.trim();
          if (text) collected.push({ type: 'text', text, _kind: 'text' });
        }
      }

      if (collected.length === 0) return EMPTY_PARTS;

      // Stability check — reuse prev if same shape
      const prev = prevRef.current;
      if (
        prev.length === collected.length &&
        collected.every((p, i) => {
          if (p._kind !== prev[i]?._kind) return false;
          if (p._kind === 'tool' && prev[i]._kind === 'tool') {
            return p.toolName === (prev[i] as ToolCallPart & { _kind: 'tool' }).toolName
              && p.status?.type === (prev[i] as ToolCallPart & { _kind: 'tool' }).status?.type;
          }
          if (p._kind === 'text' && prev[i]._kind === 'text') {
            return p.text === (prev[i] as TextPart & { _kind: 'text' }).text;
          }
          return false;
        })
      ) {
        return prev;
      }
      prevRef.current = collected;
      return collected;
    } catch {
      return EMPTY_PARTS;
    }
  });

  const toolCount = reasoningParts.filter((p) => p._kind === 'tool').length;
  if (toolCount === 0 || !viewToolsDebug) return null;

  const hasRunning = reasoningParts.some(
    (p) => p._kind === 'tool' && (p as ToolCallPart).status?.type === 'running',
  );

  const autoExpanded = hasRunning || isRunning || viewToolsDebug;
  const expanded = manualToggle !== null ? manualToggle : autoExpanded;

  const triggerLabel = hasRunning
    ? 'Using tools...'
    : `Used ${toolCount} tool${toolCount !== 1 ? 's' : ''}`;

  return (
    <div
      className={cn(
        'mb-6 mt-2 overflow-hidden rounded-lg border bg-background',
      )}
    >
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setManualToggle((v) => (v === null ? !autoExpanded : !v))}
        className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-[12px] text-foreground"
      >
        <BrainIcon className="size-3.5 shrink-0" />
        <span className={cn(hasRunning && 'shimmer shimmer-speed-80')}>{triggerLabel}</span>
        <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
          <LockIcon className="size-2.5" />
          Gorgias only
        </span>
        <ChevronDownIcon
          className={cn(
            'ml-auto size-3 shrink-0 transition-transform duration-150',
            expanded && 'rotate-180',
          )}
        />
      </button>

      {/* Body — tool rows interleaved with thinking text */}
      {expanded && (
        <ToolCallsBody
          reasoningParts={reasoningParts}
          streamingStatus={streamingStatus}
          hasPendingInterrupt={hasPendingInterrupt}
        />
      )}
    </div>
  );
}
